import { Section, Question, CheckboxGroup, SelectField, PriorityRow, WorkshopNote } from "./PortalShared";

export default function PortalPhase2({ formData, updateField, toggleCheckbox }) {
  const structureSections = [
    { key: "hero", label: "Hero", badge: "Pflicht", fields: [
      { key: "w_hero_headline", label: "Headline", ph: "Hauptaussage …" },
      { key: "w_hero_subline", label: "Subline + CTA-Text", ph: "Subline / CTA …" },
      { key: "w_hero_asset", label: "Asset-Quelle", ph: "eigene Fotos, stock, video …" },
      { key: "w_hero_owner", label: "Owner + Deadline", ph: "Wer / bis wann …" },
    ]},
    { key: "trust", label: "Trust / Social Proof", fields: [
      { key: "w_trust_logos", label: "Logos / Zahlen / Zertifikate", ph: "Welche Logos, Kennzahlen …" },
      { key: "w_trust_owner", label: "Owner + Deadline", ph: "…" },
    ]},
    { key: "problem", label: "Problem → Lösung", fields: [
      { key: "w_problem_statement", label: "Problem-Statement", ph: "Welches Problem beschreiben wir …" },
      { key: "w_solution_narrative", label: "Lösungsnarrative", ph: "Wie positionieren wir die Lösung …" },
      { key: "w_problem_asset", label: "Asset-Quelle", ph: "Icons, Text, Illustration …" },
      { key: "w_problem_owner", label: "Owner + Deadline", ph: "…" },
    ]},
    { key: "process", label: "Vorgehen / Prozess", fields: [
      { key: "w_process_method", label: "Methodik / Steps", ph: "z.B. 3-Schritt-Modell …" },
      { key: "w_process_display", label: "Darstellung", ph: "Timeline, Cards, Icons …" },
      { key: "w_process_asset", label: "Asset-Quelle", ph: "…" },
      { key: "w_process_owner", label: "Owner + Deadline", ph: "…" },
    ]},
    { key: "usecases", label: "Use Cases", badge: "opt.", fields: [
      { key: "w_usecases_branches", label: "Branchen / Anwendungsfälle", ph: "z.B. Gesundheit, Industrie …" },
      { key: "w_usecases_owner", label: "Owner + Deadline", ph: "…" },
    ]},
    { key: "testimonials", label: "Testimonials", fields: [
      { key: "w_testimonial_quote", label: "Zitat + Name / Firma", ph: "Zitat, Name, Funktion, Firma …" },
      { key: "w_testimonial_freigabe", label: "Freigabe + Deadline", ph: "Was darf publik / wer gibt frei …" },
    ]},
    { key: "faq", label: "FAQ", fields: [
      { key: "w_faq_content", label: "Top-5 Fragen + Antworten", ph: "F: … A: …", tall: true },
      { key: "w_faq_owner", label: "Owner + Deadline", ph: "…" },
    ]},
    { key: "cta", label: "CTA + Kontakt", badge: "Pflicht", fields: [
      { key: "w_cta_headline", label: "CTA-Headline", ph: "z.B. 'Bereit? Sprechen wir.' …" },
      { key: "w_cta_mechanic", label: "Conversion-Mechanik", ph: "Formular / Calendly / Telefon …" },
      { key: "w_cta_asset", label: "Asset-Quelle", ph: "…" },
      { key: "w_cta_owner", label: "Owner + Deadline", ph: "…" },
    ]},
  ];

  return (
    <>
      {/* === 04 Zielgruppen & Tonalität === */}
      <Section num="04" title="Zielgruppen & Tonalität">
        <Question label="Primäre Zielgruppe" text="Branche, Rolle, Firmengrösse, Region"
          info="Definieren Sie Ihre ideale Zielgruppe so spezifisch wie möglich — das bestimmt Sprache, Design und Ansprache.">
          <input type="text" className="portal-input" value={formData.w_primary_target || ""}
            onChange={e => updateField("w_primary_target", e.target.value)}
            placeholder="z.B. IT-Leiter / CTO, Unternehmen 50–500 MA, DACH" data-testid="field-w_primary_target" />
        </Question>

        <Question label="Entscheidungslandschaft" text="Wer ist Entscheider, Influencer, Nutzer, Blocker?"
          info="In B2B-Entscheidungen sind mehrere Rollen beteiligt. Das beeinflusst, welche Argumente auf der Seite platziert werden.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[["w_entscheider", "Entscheider: …"], ["w_influencer", "Influencer: …"],
              ["w_nutzer", "Nutzer: …"], ["w_blocker", "Blocker: …"]].map(([key, ph]) => (
              <input key={key} type="text" className="portal-input" value={formData[key] || ""}
                onChange={e => updateField(key, e.target.value)} placeholder={ph} data-testid={`field-${key}`} />
            ))}
          </div>
        </Question>

        <Question label="Top-3 Pain Points / Jobs-to-be-done"
          info="Die grössten Probleme oder Aufgaben Ihrer Zielgruppe. Diese bestimmen die Kernbotschaften der Homepage.">
          <div className="flex flex-col gap-2 mt-1">
            <PriorityRow fieldKey="w_pain_1" badge="1" placeholder="Grösster Pain Point …" formData={formData} updateField={updateField} />
            <PriorityRow fieldKey="w_pain_2" badge="2" placeholder="Zweiter Pain Point …" formData={formData} updateField={updateField} />
            <PriorityRow fieldKey="w_pain_3" badge="3" placeholder="Dritter Pain Point …" formData={formData} updateField={updateField} />
          </div>
        </Question>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Tonalität & Anrede"
            info="Wie sprechen Sie Ihre Zielgruppe an? Die Tonalität beeinflusst alle Texte auf der Homepage.">
            <SelectField fieldKey="w_tonality"
              options={["Sie — sachlich, premium", "Sie — direkt, klar", "Du — modern, zugänglich", "Du — performance-orientiert"]}
              formData={formData} updateField={updateField} />
            <input type="text" className="portal-input mt-1.5" value={formData.w_tonality_notes || ""}
              onChange={e => updateField("w_tonality_notes", e.target.value)}
              placeholder="Ergänzungen …" data-testid="field-w_tonality_notes" />
          </Question>
          <Question label="No-Gos" text="Wörter, Bildwelt, Versprechen">
            <textarea className="portal-input portal-textarea" value={formData.w_no_gos || ""}
              onChange={e => updateField("w_no_gos", e.target.value)}
              placeholder="z.B. kein 'innovativ', keine Stock-Fotos …" data-testid="field-w_no_gos" />
          </Question>
        </div>
      </Section>

      {/* === 05 Traffic & Message Match === */}
      <Section num="05" title="Traffic & Message Match">
        <Question label="Kanäle + Budget / Anteil"
          info="Die Verteilung des Marketing-Budgets auf verschiedene Kanäle. Hilft bei der Priorisierung der Seitenstruktur.">
          <textarea className="portal-input portal-textarea" value={formData.w_channel_budget || ""}
            onChange={e => updateField("w_channel_budget", e.target.value)}
            placeholder="z.B. LinkedIn 60 % (CHF 3 000/Mt.), Google Ads 30 %, SEO 10 % …" data-testid="field-w_channel_budget" />
        </Question>

        <Question label="Keywords / Ad-Angles" text="Welche Botschaften müssen 1:1 auf der Seite gespiegelt werden?"
          info="Message Match: Die Botschaft auf der Landingpage muss zur Werbeanzeige passen, damit der Besucher sich wiederfindet.">
          <textarea className="portal-input portal-textarea" value={formData.w_keywords_angles || ""}
            onChange={e => updateField("w_keywords_angles", e.target.value)}
            placeholder="z.B. 'digitale Transformation Schweiz', Key-Hooks aus Ads …" data-testid="field-w_keywords_angles" />
        </Question>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Traffic-Temperatur"
            info="Cold Traffic = Besucher, die Sie noch nicht kennen. Warm = kennen Sie bereits (z.B. via Retargeting). Das beeinflusst die nötige Überzeugungsarbeit.">
            <SelectField fieldKey="w_traffic_temp"
              options={["Hauptsächlich Cold Traffic", "Mix Cold / Warm", "Hauptsächlich Warm / Retargeting"]}
              formData={formData} updateField={updateField} />
          </Question>
          <Question label="Primäres Device">
            <SelectField fieldKey="w_primary_device"
              options={["Primär Mobile (> 60 %)", "Ausgewogen (40 / 60)", "Primär Desktop (> 60 %)"]}
              formData={formData} updateField={updateField} />
          </Question>
        </div>

        <Question label="Sprachen + Review">
          <CheckboxGroup fieldKey="w_languages"
            options={["Deutsch", "Englisch", "Französisch", "Weitere"]}
            formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
          <input type="text" className="portal-input mt-2" value={formData.w_language_review || ""}
            onChange={e => updateField("w_language_review", e.target.value)}
            placeholder="Review-Verantwortung Übersetzungen …" data-testid="field-w_language_review" />
        </Question>
      </Section>

      {/* === 06 Seitenstruktur === */}
      <Section num="06" title="Seitenstruktur — Inhalte pro Abschnitt" subtitle="Im Workshop ausfüllen">
        <WorkshopNote text="Diesen Teil füllen wir gemeinsam aus. LAB336 liefert den Strukturvorschlag — ihr bestätigt, ergänzt, korrigiert." />
        <div className="space-y-4">
          {structureSections.map(section => (
            <div key={section.key} className="border border-[#CBD5E1]">
              <div className="bg-[#F8F9FA] px-4 py-2.5 border-b border-[#CBD5E1] flex items-center gap-2">
                <span className="font-semibold text-sm text-[#0F172A]">{section.label}</span>
                {section.badge && (
                  <span className={`text-[0.625rem] font-bold uppercase px-1.5 py-0.5 ${
                    section.badge === "Pflicht" ? "bg-[#0F172A] text-[#D4A373]" : "bg-[#EEF2FF] text-[#94A3B8]"
                  }`}>{section.badge}</span>
                )}
              </div>
              <div className="p-4 space-y-2.5">
                {section.fields.map(f => (
                  <div key={f.key}>
                    <label className="text-[0.6875rem] font-semibold text-[#94A3B8] uppercase block mb-1" style={{ letterSpacing: "0.12em" }}>{f.label}</label>
                    <textarea className={`portal-input portal-textarea ${f.tall ? "tall" : ""}`}
                      value={formData[f.key] || ""} onChange={e => updateField(f.key, e.target.value)}
                      placeholder={f.ph} data-testid={`field-${f.key}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* === 07 Conversion-Design === */}
      <Section num="07" title="Conversion-Design — Formular & Booking">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Primärer CTA — exakter Wortlaut"
            info="CTA = Call to Action. Der Text auf dem wichtigsten Button Ihrer Homepage. Sollte klar, handlungsorientiert und konkret sein.">
            <input type="text" className="portal-input" value={formData.w_primary_cta || ""}
              onChange={e => updateField("w_primary_cta", e.target.value)}
              placeholder="z.B. 'Kostenloses Erstgespräch buchen'" data-testid="field-w_primary_cta" />
          </Question>
          <Question label="Conversion-Mechanik"
            info="Wie wird die Conversion technisch umgesetzt? Z.B. ein eigenes Formular, ein Calendly-Widget oder ein direkter Telefonanruf.">
            <SelectField fieldKey="w_conversion_mechanic"
              options={["Formular (eigenes)", "Cal.com", "Calendly", "Telefonnummer", "E-Mail Direktlink", "Kombination"]}
              formData={formData} updateField={updateField} />
            <input type="text" className="portal-input mt-1.5" value={formData.w_conversion_details || ""}
              onChange={e => updateField("w_conversion_details", e.target.value)}
              placeholder="Link / Details …" data-testid="field-w_conversion_details" />
          </Question>
        </div>

        <Question label="Formularfelder" text="Pflicht vs. optional">
          <textarea className="portal-input portal-textarea" value={formData.w_form_fields || ""}
            onChange={e => updateField("w_form_fields", e.target.value)}
            placeholder={"Pflicht: Vorname, Nachname, E-Mail, Unternehmen …\nOptional: Telefon, Nachricht …"}
            data-testid="field-w_form_fields" />
        </Question>

        <Question label="Qualifizierungsfragen" text="Budget, Timing, Use Case, Teamgrösse etc."
          info="Fragen im Formular, die helfen, die Qualität der Leads einzuschätzen — z.B. Unternehmensgrösse oder Projektbudget.">
          <textarea className="portal-input portal-textarea" value={formData.w_qualification_questions || ""}
            onChange={e => updateField("w_qualification_questions", e.target.value)}
            placeholder="z.B. 'Wie gross ist Ihr Unternehmen?', 'Wann soll gestartet werden?' …"
            data-testid="field-w_qualification_questions" />
        </Question>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Spam-Schutz">
            <SelectField fieldKey="w_spam_protection"
              options={["Honeypot (unsichtbar)", "hCaptcha", "reCAPTCHA v3", "Turnstile (Cloudflare)"]}
              formData={formData} updateField={updateField} />
          </Question>
          <Question label="SLA nach Submit"
            info="Service Level Agreement — wie schnell antworten Sie auf eine Anfrage? Das schafft Erwartungen beim Kunden.">
            <input type="text" className="portal-input" value={formData.w_sla_response || ""}
              onChange={e => updateField("w_sla_response", e.target.value)}
              placeholder="z.B. Antwort innerhalb 1 WT, zuständig: …" data-testid="field-w_sla_response" />
          </Question>
        </div>

        <Question label="Danke-Seite" text="Was passiert nach dem Submit?">
          <textarea className="portal-input portal-textarea" value={formData.w_thank_you_page || ""}
            onChange={e => updateField("w_thank_you_page", e.target.value)}
            placeholder="z.B. Weiterleitung /danke mit Calendly-Embed, Bestätigungs-E-Mail …"
            data-testid="field-w_thank_you_page" />
        </Question>
      </Section>

      {/* === 08 Proof & Vertrauen === */}
      <Section num="08" title="Proof & Vertrauen">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Referenzlogos" text="Vorhanden? Freigabe erteilt?"
            info="Logos von Kunden oder Partnern, die als Vertrauenssignal auf der Seite gezeigt werden dürfen.">
            <textarea className="portal-input portal-textarea" value={formData.w_reference_logos || ""}
              onChange={e => updateField("w_reference_logos", e.target.value)}
              placeholder="Anzahl, Namen, Format, Freigabe …" data-testid="field-w_reference_logos" />
          </Question>
          <Question label="Testimonials" text="Name / Funktion / Firma erlaubt?">
            <textarea className="portal-input portal-textarea" value={formData.w_testimonials_proof || ""}
              onChange={e => updateField("w_testimonials_proof", e.target.value)}
              placeholder="Wer darf zitiert werden …" data-testid="field-w_testimonials_proof" />
          </Question>
          <Question label="Case Studies / Zahlen" text="Was darf publiziert werden?">
            <textarea className="portal-input portal-textarea" value={formData.w_case_studies || ""}
              onChange={e => updateField("w_case_studies", e.target.value)}
              placeholder="z.B. '40 % Zeitersparnis, Kunde X anonymisiert' …" data-testid="field-w_case_studies" />
          </Question>
          <Question label="Zertifikate / Partner / Awards">
            <textarea className="portal-input portal-textarea" value={formData.w_certificates || ""}
              onChange={e => updateField("w_certificates", e.target.value)}
              placeholder="z.B. Microsoft Partner, ISO 27001 …" data-testid="field-w_certificates" />
          </Question>
        </div>

        <Question label="Datenschutz & Security Statement" text="Wichtige Hinweise für CH / EU (nDSG, DSGVO)?"
          info="nDSG = neues Schweizer Datenschutzgesetz, DSGVO = EU-Datenschutzgrundverordnung. Relevant für Hosting, Datenverarbeitung und Cookie-Consent.">
          <textarea className="portal-input portal-textarea" value={formData.w_privacy_statement || ""}
            onChange={e => updateField("w_privacy_statement", e.target.value)}
            placeholder="z.B. Hosting in CH, DSGVO-konform, DSE vorhanden …" data-testid="field-w_privacy_statement" />
        </Question>
      </Section>
    </>
  );
}
